export type CsvDataType = {
  headers: string[];
  data: string[][];
};

/**
 * Transforms a list of objects into an array of arrays with header and data
 * which can be used for an csv export.
 *
 * @param objectList The given list of objects should be transformed
 * @return The transformed headers and data
 */
export const transformToCSVData = (objectList: object[]): CsvDataType => {
  const csvData: CsvDataType = { headers: [], data: [] };

  if (!objectList || objectList.length === 0) return csvData;

  // set headers
  csvData.headers = Object.keys(objectList[0]);

  // set data
  objectList.forEach((item) => {
    csvData.data.push(
      Object.values(item).map((value) => (value !== undefined && value !== null ? value.toString() : "no value"))
    );
  });

  return csvData;
};

type DownloadCsvFileOptions = {
  includeHeaders?: boolean;
  separator?: string;
};

/**
 * Quotes and escapes a CSV field per RFC 4180 when it contains special characters.
 * TODO: Temporary until SEPA-XML functionality is implemented.
 */
export const escapeCsvField = (value: string, separator: string): string => {
  // Make sure that special characters that could trigger spreadsheet formulas are escaped.
  const sanitizedValue = /^\s*[=+\-@]/.test(value) ? `'${value}` : value;

  if (sanitizedValue.includes(separator) || sanitizedValue.includes('"') || /[\r\n]/.test(sanitizedValue)) {
    return `"${sanitizedValue.replace(/"/g, '""')}"`;
  }
  return sanitizedValue;
};

/**
 * Triggers a browser download of the given object list as a CSV file.
 * TODO: Temporary until SEPA-XML functionality is implemented.
 */
export const downloadCsvFile = (objectList: object[], fileName: string, options: DownloadCsvFileOptions = {}): void => {
  const { includeHeaders = true, separator = ";" } = options;

  if (!objectList || objectList.length === 0) {
    return;
  }

  const csvData = transformToCSVData(objectList);
  const rows = includeHeaders ? [csvData.headers, ...csvData.data] : csvData.data;

  const content = rows.map((row) => row.map((value) => escapeCsvField(value, separator)).join(separator)).join("\n");
  const blob = new Blob([content], { type: "text/csv;charset=utf-8;" });
  const url = URL.createObjectURL(blob);
  const link = document.createElement("a");
  link.href = url;
  link.download = fileName.endsWith(".csv") ? fileName : `${fileName}.csv`;
  document.body.appendChild(link);
  link.click();
  document.body.removeChild(link);
  URL.revokeObjectURL(url);
};
